import { AI_VISIBILITY } from "../../../../../src/shared/enums.js";
import { extractTextFromTipTap } from "../../../../../src/shared/schemas/CharacterSchema.js";
import { findEntityMatches } from "../../../../../src/shared/visualizeEntities.js";
import {
  harvestStoryBibleEntityNamesFromEntities,
  isNotCommonWord,
} from "../../../../../src/shared/storyBibleEntityNames.js";
import { isPatternedChapterManuscriptTitle } from "../../../../../src/ai/persistentChat/documentMentionAmbiguity.js";
import type { MentionItem } from "./mentionSearch";
import { characterMentionLabel, worldbuildingMentionLabel } from "./mentionSearch";

type CharacterLike = { id: string; aiVisibility?: string; name?: unknown; otherNames?: unknown; displayName?: string };
type WorldbuildingLike = { id: string; aiVisibility?: string; name?: unknown; otherNames?: unknown; displayName?: string };
type DocumentLike = { id: string; title?: string; bookTitle?: string };
type SeriesBookLike = { projectId: string; title?: string; seriesOrder?: number | null };
type SeriesBookDocumentLike = { id: string; title?: string; bookProjectId: string; bookTitle?: string };

function normalizeEdgePunctuationToken(token: string): string {
  return token.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, "");
}

function addTokenAndHyphenVariants(tokens: Set<string>, token: string): void {
  const trimmed = token.trim();
  if (!trimmed) return;
  tokens.add(trimmed);

  const normalized = normalizeEdgePunctuationToken(trimmed);
  if (normalized) {
    tokens.add(normalized);
  }

  const dashNormalized = normalized.replace(/[–—]/g, "-");
  if (dashNormalized) {
    tokens.add(dashNormalized);
  }

  const dashParts = dashNormalized.split("-").map((part) => normalizeEdgePunctuationToken(part)).filter(Boolean);
  if (dashParts.length < 2) return;

  // Include incremental hyphen prefixes so "parts-per" can match titles like "parts-per-billion-...".
  let prefix = dashParts[0];
  tokens.add(prefix);
  for (let i = 1; i < dashParts.length; i++) {
    prefix = `${prefix}-${dashParts[i]}`;
    tokens.add(prefix);
    tokens.add(dashParts[i]);
  }
}

/**
 * Maps lowercased labels (full names, titles, tokens) to mention items that use that label.
 * Ambiguous labels yield multiple items (e.g. two documents titled the same).
 */
export function buildLabelToMentionItemsMap(options: {
  characters?: CharacterLike[];
  worldbuilding?: WorldbuildingLike[];
  documents?: DocumentLike[];
  seriesBooks?: SeriesBookLike[];
  seriesBookDocuments?: SeriesBookDocumentLike[];
  hasOutline?: boolean;
  /**
   * When false, character/worldbuilding labels are omitted so auto-detect matches the `#` menu
   * (`filterMentionItems`), which hides story-bible entries until the panel is active.
   * @default true — Draft and tests keep full maps unless callers pass false.
   */
  isStoryBibleActive?: boolean;
}): Map<string, MentionItem[]> {
  const {
    characters = [],
    worldbuilding = [],
    documents = [],
    seriesBooks = [],
    seriesBookDocuments = [],
    hasOutline = false,
    isStoryBibleActive = true,
  } = options;

  const map = new Map<string, MentionItem[]>();

  const pushLabel = (label: string, item: MentionItem) => {
    const key = label.trim().toLowerCase();
    if (!key) return;
    const existing = map.get(key) ?? [];
    if (existing.some((x) => x.id === item.id)) return;
    existing.push(item);
    map.set(key, existing);
  };

  /** Draft parity: do not register trivial English tokens from multi-word titles/names (avoids conflict spam). */
  const pushWordToken = (token: string, item: MentionItem) => {
    const variants = new Set<string>();
    addTokenAndHyphenVariants(variants, token);
    for (const variant of variants) {
      if (isNotCommonWord(variant)) {
        pushLabel(variant, item);
      }
    }
  };

  // Auto-detect "Outline" whenever the project has outline data (Story Bible panel may be collapsed).
  if (hasOutline) {
    pushLabel("Outline", { id: "outline::current", display: "Outline", type: "outline" });
  }

  for (const doc of documents) {
    const title = (doc.title || "").trim();
    if (!title) continue;
    const bookTitle = typeof doc.bookTitle === "string" ? doc.bookTitle.trim() : "";
    const item: MentionItem = {
      id: `document::${doc.id}`,
      display: title,
      type: "document",
      ...(bookTitle ? { subtitle: bookTitle } : {}),
      ...(bookTitle ? { mentionLabel: `${title} (${bookTitle})` } : {}),
    };
    pushLabel(title, item);
    for (const token of title.split(/\s+/).filter(Boolean)) {
      const tokenKey = normalizeEdgePunctuationToken(token).toLowerCase();
      if (tokenKey === "chapter" && isPatternedChapterManuscriptTitle(title)) {
        continue;
      }
      // Titles like "Chapter 1: Foo" split into `1:`; registering that label blocks shorter `1` in longest-first matching.
      if (isPatternedChapterManuscriptTitle(title) && /^\d+:$/.test(token)) {
        pushWordToken(tokenKey, item);
        continue;
      }
      pushWordToken(token, item);
    }
  }

  // Add series books for auto-detection
  for (const book of seriesBooks) {
    const title = (book.title || "").trim();
    if (!title) continue;
    const item: MentionItem = { id: `series::${book.projectId}`, display: title, type: "series" };
    pushLabel(title, item);
    for (const token of title.split(/\s+/).filter(Boolean)) {
      pushWordToken(token, item);
    }
  }

  // Series book documents — label map matches document title; UI shows book on second line via subtitle
  for (const doc of seriesBookDocuments) {
    const title = (doc.title || "").trim();
    if (!title) continue;
    const bookLabel = (doc.bookTitle || "Series Book").trim();
    const item: MentionItem = {
      id: `series_document::${doc.bookProjectId}::${doc.id}`,
      display: title,
      type: "document",
      subtitle: bookLabel,
      mentionLabel: `${title} (${bookLabel})`,
    };
    pushLabel(title, item);
    for (const token of title.split(/\s+/).filter(Boolean)) {
      const tokenKey = normalizeEdgePunctuationToken(token).toLowerCase();
      if (tokenKey === "chapter" && isPatternedChapterManuscriptTitle(title)) {
        continue;
      }
      if (isPatternedChapterManuscriptTitle(title) && /^\d+:$/.test(token)) {
        pushWordToken(tokenKey, item);
        continue;
      }
      pushWordToken(token, item);
    }
  }

  if (isStoryBibleActive) {
    for (const char of characters) {
      if (char.aiVisibility === AI_VISIBILITY.HIDDEN) continue;
      const label = characterMentionLabel(char);
      const item: MentionItem = { id: `character::${char.id}`, display: label, type: "character" };
      pushLabel(label, item);
      for (const token of label.split(/\s+/).filter(Boolean)) {
        pushWordToken(token, item);
      }
      /** TipTap `name` may hold the full name while `displayName` is only first or last — match on every word. */
      const namePlain =
        char.name != null ? extractTextFromTipTap(char.name as Parameters<typeof extractTextFromTipTap>[0]).trim() : "";
      if (namePlain) {
        pushLabel(namePlain, item);
        for (const token of namePlain.split(/\s+/).filter(Boolean)) {
          pushWordToken(token, item);
        }
      }
      const otherNames = extractTextFromTipTap(char.otherNames as Parameters<typeof extractTextFromTipTap>[0])
        .split(/[,;]/)
        .map((n) => n.trim())
        .filter(Boolean);
      for (const on of otherNames) {
        pushLabel(on, item);
      }
    }

    for (const entry of worldbuilding) {
      if (entry.aiVisibility === AI_VISIBILITY.HIDDEN) continue;
      const label = worldbuildingMentionLabel(entry);
      const item: MentionItem = { id: `worldbuilding::${entry.id}`, display: label, type: "worldbuilding" };
      pushLabel(label, item);
      for (const token of label.split(/\s+/).filter(Boolean)) {
        pushWordToken(token, item);
      }
      const entryNamePlain =
        entry.name != null ? extractTextFromTipTap(entry.name as Parameters<typeof extractTextFromTipTap>[0]).trim() : "";
      if (entryNamePlain) {
        pushLabel(entryNamePlain, item);
        for (const token of entryNamePlain.split(/\s+/).filter(Boolean)) {
          pushWordToken(token, item);
        }
      }
      const otherNames = extractTextFromTipTap(entry.otherNames as Parameters<typeof extractTextFromTipTap>[0])
        .split(/[,;]/)
        .map((n) => n.trim())
        .filter(Boolean);
      for (const on of otherNames) {
        pushLabel(on, item);
      }
    }
  }

  return map;
}

/**
 * Adds labels from {@link harvestStoryBibleEntityNamesFromEntities} (Draft parity) so short tokens
 * like a first name match when they are part of a character/worldbuilding/document title set.
 */
export function extendLabelMapWithHarvestedStoryBibleTokens(
  map: Map<string, MentionItem[]>,
  characters: CharacterLike[],
  worldbuilding: WorldbuildingLike[],
  documents: DocumentLike[] = [],
  options?: { isStoryBibleActive?: boolean; seriesBooks?: SeriesBookLike[]; seriesBookDocuments?: SeriesBookDocumentLike[] },
): void {
  const seriesBooks = options?.seriesBooks ?? [];
  const seriesBookDocuments = options?.seriesBookDocuments ?? [];
  const isStoryBibleActive = options?.isStoryBibleActive ?? true;

  const harvested = new Set(
    harvestStoryBibleEntityNamesFromEntities({
      charactersEntities: characters,
      worldbuildingEntries: worldbuilding,
      documents,
    }),
  );

  const charTokenSets = !isStoryBibleActive
    ? []
    : (characters || [])
    .filter((c) => c.aiVisibility !== AI_VISIBILITY.HIDDEN)
    .map((c) => {
      const label = characterMentionLabel(c);
      const tokens = new Set<string>();
      tokens.add(label);
      label.split(/\s+/).filter(Boolean).forEach((w) => tokens.add(w));
      const namePlain =
        c.name != null ? extractTextFromTipTap(c.name as Parameters<typeof extractTextFromTipTap>[0]).trim() : "";
      if (namePlain) {
        tokens.add(namePlain);
        namePlain.split(/\s+/).filter(Boolean).forEach((w) => tokens.add(w));
      }
      extractTextFromTipTap(c.otherNames as Parameters<typeof extractTextFromTipTap>[0])
        .split(/[,;]/)
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((x) => tokens.add(x));
      return { item: { id: `character::${c.id}`, display: label, type: "character" as const }, tokens };
    });

  const wbTokenSets = !isStoryBibleActive
    ? []
    : (worldbuilding || [])
    .filter((e) => e.aiVisibility !== AI_VISIBILITY.HIDDEN)
    .map((e) => {
      const label = worldbuildingMentionLabel(e);
      const tokens = new Set<string>();
      tokens.add(label);
      label.split(/\s+/).filter(Boolean).forEach((w) => tokens.add(w));
      const entryNamePlain =
        e.name != null ? extractTextFromTipTap(e.name as Parameters<typeof extractTextFromTipTap>[0]).trim() : "";
      if (entryNamePlain) {
        tokens.add(entryNamePlain);
        entryNamePlain.split(/\s+/).filter(Boolean).forEach((w) => tokens.add(w));
      }
      extractTextFromTipTap(e.otherNames as Parameters<typeof extractTextFromTipTap>[0])
        .split(/[,;]/)
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((x) => tokens.add(x));
      return { item: { id: `worldbuilding::${e.id}`, display: label, type: "worldbuilding" as const }, tokens };
    });

  const documentTokenSets = (documents || [])
    .map((d) => {
      const title = (d.title || "").trim();
      if (!title) return null;
      const bookTitle = typeof d.bookTitle === "string" ? d.bookTitle.trim() : "";
      const item = {
        id: `document::${d.id}`,
        display: title,
        type: "document" as const,
        ...(bookTitle ? { subtitle: bookTitle } : {}),
        ...(bookTitle ? { mentionLabel: `${title} (${bookTitle})` } : {}),
      };
      const tokens = new Set<string>();
      tokens.add(title);
      title.split(/\s+/).filter(Boolean).forEach((w) => {
        const wKey = normalizeEdgePunctuationToken(w).toLowerCase();
        if (wKey === "chapter" && isPatternedChapterManuscriptTitle(title)) return;
        if (isPatternedChapterManuscriptTitle(title) && /^\d+:$/.test(w)) {
          addTokenAndHyphenVariants(tokens, wKey);
          return;
        }
        addTokenAndHyphenVariants(tokens, w);
      });
      return { item, tokens };
    })
    .filter(
      (x): x is { item: MentionItem; tokens: Set<string> } =>
        x != null,
    );

  const seriesBookTokenSets = (seriesBooks || [])
    .map((b) => {
      const title = (b.title || "").trim();
      if (!title) return null;
      const item = { id: `series::${b.projectId}`, display: title, type: "series" as const };
      const tokens = new Set<string>();
      tokens.add(title);
      title.split(/\s+/).filter(Boolean).forEach((w) => addTokenAndHyphenVariants(tokens, w));
      return { item, tokens };
    })
    .filter(
      (x): x is { item: MentionItem; tokens: Set<string> } =>
        x != null,
    );

  const seriesBookDocumentTokenSets = (seriesBookDocuments || [])
    .map((d) => {
      const title = (d.title || "").trim();
      if (!title) return null;
      const bookLabel = (d.bookTitle || "Series Book").trim();
      const item = {
        id: `series_document::${d.bookProjectId}::${d.id}`,
        display: title,
        type: "document" as const,
        subtitle: bookLabel,
        mentionLabel: `${title} (${bookLabel})`,
      };
      const tokens = new Set<string>();
      tokens.add(title);
      title.split(/\s+/).filter(Boolean).forEach((w) => {
        const wKey = normalizeEdgePunctuationToken(w).toLowerCase();
        if (wKey === "chapter" && isPatternedChapterManuscriptTitle(title)) return;
        if (isPatternedChapterManuscriptTitle(title) && /^\d+:$/.test(w)) {
          addTokenAndHyphenVariants(tokens, wKey);
          return;
        }
        addTokenAndHyphenVariants(tokens, w);
      });
      return { item, tokens };
    })
    .filter(
      (x): x is { item: MentionItem; tokens: Set<string> } =>
        x != null,
    );

  for (const token of harvested) {
    const key = token.trim().toLowerCase();
    if (!key) continue;

    for (const { item, tokens } of charTokenSets) {
      if ([...tokens].some((t) => t.toLowerCase() === key)) {
        const existing = map.get(key) ?? [];
        if (!existing.some((x) => x.id === item.id)) {
          map.set(key, [...existing, item]);
        }
      }
    }
    for (const { item, tokens } of wbTokenSets) {
      if ([...tokens].some((t) => t.toLowerCase() === key)) {
        const existing = map.get(key) ?? [];
        if (!existing.some((x) => x.id === item.id)) {
          map.set(key, [...existing, item]);
        }
      }
    }
    for (const { item, tokens } of documentTokenSets) {
      if ([...tokens].some((t) => t.toLowerCase() === key)) {
        const existing = map.get(key) ?? [];
        if (!existing.some((x) => x.id === item.id)) {
          map.set(key, [...existing, item]);
        }
      }
    }
    for (const { item, tokens } of seriesBookTokenSets) {
      if ([...tokens].some((t) => t.toLowerCase() === key)) {
        const existing = map.get(key) ?? [];
        if (!existing.some((x) => x.id === item.id)) {
          map.set(key, [...existing, item]);
        }
      }
    }
    for (const { item, tokens } of seriesBookDocumentTokenSets) {
      if ([...tokens].some((t) => t.toLowerCase() === key)) {
        const existing = map.get(key) ?? [];
        if (!existing.some((x) => x.id === item.id)) {
          map.set(key, [...existing, item]);
        }
      }
    }
  }
}

/** Sorted longest-first unique labels for whole-word regex matching (Draft-style). */
export function sortedMatchLabelsFromMap(labelToItems: Map<string, MentionItem[]>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of labelToItems.keys()) {
    const trimmed = key.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  out.sort((a, b) => b.length - a.length);
  return out;
}

export type EntityLinkSpec = {
  name: string;
  entityId: string | null;
};

/** Draft entity visualization specs: unique labels link, ambiguous labels stay visual-only. */
export function entityLinkSpecsFromLabelMap(labelToItems: Map<string, MentionItem[]>, labels = sortedMatchLabelsFromMap(labelToItems)): EntityLinkSpec[] {
  const out: EntityLinkSpec[] = [];

  for (const label of labels) {
    const trimmed = label.trim();
    if (!trimmed) continue;
    const items = labelToItems.get(trimmed.toLowerCase()) ?? [];
    out.push({
      name: trimmed,
      entityId: items.length === 1 ? items[0].id : null,
    });
  }

  return out;
}

export type AutoMentionMatch = {
  start: number;
  end: number;
  matchedText: string;
  items: MentionItem[];
};

/**
 * Non-overlapping matches in `text`, longest labels first (same spirit as Draft entity marks).
 */
export function findAutoMentionMatchesInPlainText(text: string, labelToItems: Map<string, MentionItem[]>): AutoMentionMatch[] {
  const labels = sortedMatchLabelsFromMap(labelToItems);
  if (!text || labels.length === 0) return [];

  const used = new Array(text.length).fill(false);
  const matches: AutoMentionMatch[] = [];

  for (const label of labels) {
    const items = labelToItems.get(label.toLowerCase());
    if (!items?.length) continue;

    const found = findEntityMatches(label, text);
    for (const { start, end } of found) {
      let overlap = false;
      for (let i = start; i < end; i++) {
        if (used[i]) {
          overlap = true;
          break;
        }
      }
      if (overlap) continue;
      for (let i = start; i < end; i++) used[i] = true;
      if (/[?!]/.test(text[end] ?? "")) continue;
      matches.push({
        start,
        end,
        matchedText: text.slice(start, end),
        items: [...items],
      });
    }
  }

  matches.sort((a, b) => a.start - b.start);
  return matches;
}
